import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError, formatMoney, formatDate, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import InlineStatus from "@/components/InlineStatus";
import EditableNote from "@/components/EditableNote";
import { ColocacionBadge, LiquidacionBadge } from "@/components/ColocacionBadge";
import { PrioridadToggle } from "@/components/PrioridadToggle";
import { GuiasSeg, ColResumenAlertas } from "@/components/GuiasSeg";
import { FechaHist, DireccionEdit } from "@/components/FechaHist";
import { FABRICACION, EMPAQUE, ENTREGA } from "@/lib/statuses";
import { ProductosCell, FechasCell, PagoCell, Falta, faltaFab, faltaTodos, faltaEntrega } from "@/components/pedidos/HojaCeldas";
import { useAuth, hasRole } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, ClipboardList, Search, ExternalLink, FileSpreadsheet, AlertTriangle, Pencil, Receipt, ChevronLeft, ChevronRight } from "lucide-react";
import { rowClassForAtraso, atrasoInfo } from "@/lib/atraso";
import DateRangeFilter from "@/components/DateRangeFilter";

const ESTADOS = ["", "creado", "fabricacion", "empaque", "entrega", "facturado", "pagado", "cancelado"];

const TIPO_TABS = [
  { value: "", label: "Todos", count: (o) => true },
  { value: "pend_ciudad", label: "Pendientes de entregar en la Ciudad", count: (o) => o.envio_tipo === "ciudad" && o.estado_entrega !== "entregado" && o.estado !== "cancelado" && !o.anulado },
  { value: "pend_colocacion", label: "Pendientes de colocación para Departamentos", count: (o) => o.envio_tipo !== "ciudad" && o.estado_entrega !== "entregado" && o.estado !== "cancelado" && !o.anulado && !(o.col_resumen?.total && o.col_resumen.colocados >= o.col_resumen.total) },
  { value: "ciudad", label: "Entregas Ciudad", count: (o) => o.envio_tipo === "ciudad" },
  { value: "departamento", label: "Entregas Departamento", count: (o) => o.envio_tipo !== "ciudad" },
  { value: "pendientes", label: "Aún no entregados", count: (o) => o.estado_entrega !== "entregado" && o.estado !== "cancelado" },
];

export default function Pedidos() {
  const { user } = useAuth();
  // Sync de filtros con URL para que el botón "Volver" desde PedidoDetalle
  // recupere exactamente la pestaña, búsqueda y página desde donde se entró.
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [shipping, setShipping] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [promos, setPromos] = useState([]);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [pendienteFact, setPendienteFact] = useState(searchParams.get("pf") === "1"); // Filtro global "pendientes de facturar"
  const [fDesde, setFDesde] = useState(searchParams.get("desde") || "");
  const [fHasta, setFHasta] = useState(searchParams.get("hasta") || "");
  const [estado, setEstado] = useState(searchParams.get("estado") || "");
  const [tipoFilter, setTipoFilter] = useState(searchParams.get("tipo") || ""); // "" | "ciudad" | "departamento" | "pendientes"
  const [mineOnly, setMineOnly] = useState(searchParams.get("mine") === "1");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());

  // Form state
  const [clienteId, setClienteId] = useState("");
  const [newCliOpen, setNewCliOpen] = useState(false);
  const [newCli, setNewCli] = useState({ nombre: "", telefono: "", telefono2: "", direccion: "" });
  const [direccion, setDireccion] = useState("");
  const [fechaEntrega, setFechaEntrega] = useState("");
  const [fechaEntregaHasta, setFechaEntregaHasta] = useState("");
  const [envioId, setEnvioId] = useState("");
  const [items, setItems] = useState([]);
  const [notas, setNotas] = useState("");
  const [anticipo, setAnticipo] = useState(0);
  const [anticipoMetodo, setAnticipoMetodo] = useState("");
  const [anticipoBanco, setAnticipoBanco] = useState("");
  const [anticipoCajaId, setAnticipoCajaId] = useState("");
  const [anticipoAuth, setAnticipoAuth] = useState("");
  const [anticipoBoleta, setAnticipoBoleta] = useState("");
  const [bancos, setBancos] = useState([]);
  const [tipoEnvio, setTipoEnvio] = useState(""); // OBLIGATORIO al crear
  const [modifiersCatalog, setModifiersCatalog] = useState([]);
  const [cajas, setCajas] = useState([]);
  const [anticipoConfirmado, setAnticipoConfirmado] = useState(false); // Fase F 2.1 - recordatorio
  const [showAnticipoReminder, setShowAnticipoReminder] = useState(false);
  const [showFactRapida, setShowFactRapida] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyReporte, setBusyReporte] = useState(false);
  // Vista Actual (por defecto) vs Archivados (pedidos con cierre_id)
  const [vista, setVista] = useState(searchParams.get("vista") || "actual"); // actual | archivados
  // Paginación en servidor: 30 pedidos por página.
  const [page, setPage] = useState(Number(searchParams.get("page") || 1) || 1);
  const [pageMeta, setPageMeta] = useState({ total: 0, total_pages: 1, page: 1 });
  const [verN, setVerN] = useState(150);
  useEffect(() => { setVerN(150); }, [tipoFilter, estado, q, vista, fDesde, fHasta]);

  // Serializar filtros → URL. Solo se persisten los que NO están en su default.
  useEffect(() => {
    const next = new URLSearchParams();
    if (tipoFilter) next.set("tipo", tipoFilter);
    if (vista !== "actual") next.set("vista", vista);
    if (estado) next.set("estado", estado);
    if (q) next.set("q", q);
    if (pendienteFact) next.set("pf", "1");
    if (mineOnly) next.set("mine", "1");
    if (fDesde) next.set("desde", fDesde);
    if (fHasta) next.set("hasta", fHasta);
    if (page > 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });
     
  }, [tipoFilter, vista, estado, q, pendienteFact, mineOnly, fDesde, fHasta, page]);

  const load = async () => {
    const params = {
      estado: estado || undefined,
      q: q || undefined,
      tipo: ["ciudad", "departamento", "pend_ciudad", "pend_colocacion"].includes(tipoFilter) ? tipoFilter : undefined,
      pendientes_entrega: tipoFilter === "pendientes" || undefined,
      pendiente_facturar_all: pendienteFact || undefined,
      mine: mineOnly || undefined,
      historial: vista === "archivados" ? true : undefined,
      page,
      page_size: 10000,
      ligero: true,
    };
    const [o, p, s, c, pr, bk, mods, cjs] = await Promise.all([
      api.get("/orders", { params }),
      api.get("/products"),
      api.get("/shipping"),
      api.get("/customers"),
      api.get("/promotions/active"),
      api.get("/bancos"),
      api.get("/modifiers", { params: { solo_activos: true } }).catch(() => ({ data: [] })),
      api.get("/cajas").catch(() => ({ data: [] })),
    ]);
    // Backend ahora devuelve {items, total, page, page_size, total_pages}
    const ordersData = Array.isArray(o.data) ? o.data : (o.data.items || []);
    setOrders(ordersData);
    setPageMeta(Array.isArray(o.data)
      ? { total: ordersData.length, total_pages: 1, page: 1 }
      : { total: o.data.total || 0, total_pages: o.data.total_pages || 1, page: o.data.page || 1 });
    setProducts(p.data);
    setShipping(s.data);
    setCustomers(c.data);
    setPromos(pr.data);
    setBancos((bk.data || []).filter((b) => b.activo !== false));
    setModifiersCatalog(mods.data || []);
    setCajas((cjs.data || []).filter((x) => x.activo !== false));
  };
  useEffect(() => { load(); }, [estado, tipoFilter, mineOnly, pendienteFact, page, vista]);
  // Al cambiar filtros, vista o búsqueda, volver a la página 1.
  useEffect(() => { setPage(1); }, [estado, tipoFilter, mineOnly, pendienteFact, q, vista]);

  const resetForm = () => {
    setClienteId(""); setDireccion(""); setFechaEntrega(""); setFechaEntregaHasta(""); setEnvioId(""); setItems([]); setNotas("");
    setAnticipo(0); setAnticipoMetodo(""); setAnticipoBanco(""); setAnticipoCajaId(""); setAnticipoAuth(""); setAnticipoBoleta("");
    setTipoEnvio("");
  };

  const addItem = () => {
    setItems([...items, { key: crypto.randomUUID(), product_id: "", variant_id: "", color: "", estilo: "", tela: "", tamano: "", cantidad: 1, precio_base: 0, precio_unit: 0, descuento: 0, modificadores: [] }]);
  };

  const updateItem = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    setItems(next);
  };

  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const crearNuevoCliente = async () => {
    if (!newCli.nombre.trim()) return toast.error("Nombre requerido");
    try {
      const { data } = await api.post("/customers", {
        nombre: newCli.nombre.trim(),
        telefono: newCli.telefono.trim(),
        telefono2: newCli.telefono2.trim(),
        direccion: newCli.direccion.trim(),
        email: "", ciudad: "", notas: "", descuento_pct: 0,
      });
      toast.success("Cliente creado");
      // Refresh list and preselect
      const { data: cs } = await api.get("/customers");
      setCustomers(cs);
      setClienteId(data.id);
      if (data.direccion && !direccion) setDireccion(data.direccion);
      setNewCliOpen(false);
      setNewCli({ nombre: "", telefono: "", telefono2: "", direccion: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const exportSelected = async () => {
    if (selected.size === 0) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/reports/export-seleccionados.xlsx`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ ids: Array.from(selected), tipo: "pedidos" }),
      });
      if (!res.ok) throw new Error("Error al exportar");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `pedidos_seleccion_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 60000);
      toast.success(`${selected.size} pedidos exportados`);
    } catch (e) {
      toast.error("No se pudo exportar");
    }
  };

  const exportReporteRuta = async () => {
    if (selected.size === 0) return toast.error("Selecciona al menos un pedido");
    setBusyReporte(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/reports/plan-ruta.xlsx`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ order_ids: Array.from(selected) }),
      });
      if (!res.ok) throw new Error("Error al exportar Reporte de Ruta");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `plan_ruta_${stamp}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(a.href), 60000);
      toast.success(`Reporte de Ruta generado (${selected.size} pedidos). Los pedidos de Ciudad recibieron un código RUT-YYYY-XXXXX.`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyReporte(false);
    }
  };

  const onClienteChange = (cid) => {
    setClienteId(cid);
    const c = customers.find((x) => x.id === cid);
    if (c) setDireccion(c.direccion || "");  // siempre la del cliente elegido (antes se quedaba la del cliente anterior)
  };

  const onProductChange = (idx, pid) => {
    const p = products.find((x) => x.id === pid);
    if (!p) { updateItem(idx, { product_id: pid, precio_unit: 0, precio_base: 0, descuento: 0, variant_id: "", color: "", estilo: "", tela: "", tamano: "" }); return; }
    updateItem(idx, {
      product_id: pid,
      precio_base: Number(p.precio || 0),
      precio_unit: Number(p.precio || 0),
      descuento: Number(p.descuento || 0),
      variant_id: "",
      color: "", estilo: "", tela: "", tamano: "",
    });
  };

  const recomputePrice = (patch, product) => {
    // Compute precio_unit = precio_base + sum(extras of selected variantes)
    if (!product) return patch;
    const base = Number(product.precio || 0);
    const findExtra = (opts, val) => {
      const opt = (opts || []).find((o) => o.nombre === val);
      return opt ? Number(opt.precio_extra || 0) : 0;
    };
    const color = patch.color ?? "";
    const estilo = patch.estilo ?? "";
    const tela = patch.tela ?? "";
    const tamano = patch.tamano ?? "";
    const extra = findExtra(product.opciones_color, color) +
                  findExtra(product.opciones_estilo, estilo) +
                  findExtra(product.opciones_tela, tela) +
                  findExtra(product.opciones_tamano, tamano);
    return { ...patch, precio_base: base, precio_unit: base + extra };
  };

  const onVariantChange = (idx, axis, value) => {
    const it = items[idx];
    const product = products.find((p) => p.id === it.product_id);
    const merged = { color: it.color, estilo: it.estilo, tela: it.tela, tamano: it.tamano, [axis]: value };
    updateItem(idx, recomputePrice(merged, product));
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    for (const it of items) {
      const price = Number(it.precio_unit || 0) * (1 - Number(it.descuento || 0) / 100);
      subtotal += price * Number(it.cantidad || 0);
    }
    const envio = shipping.find((s) => s.id === envioId);
    const envioPrecio = envio ? Number(envio.precio || 0) : 0;
    return { subtotal, envioPrecio, total: subtotal + envioPrecio };
  }, [items, envioId, shipping]);

  const save = async () => {
    if (!clienteId) return toast.error("Selecciona un cliente");
    if (!direccion.trim()) return toast.error("Ingresa la dirección");
    if (!fechaEntrega) return toast.error("La fecha de entrega Desde es obligatoria");
    if (!fechaEntregaHasta) return toast.error("La fecha de entrega Hasta es obligatoria");
    if (fechaEntregaHasta < fechaEntrega) return toast.error("La fecha Hasta debe ser mayor o igual a Desde");
    if (!tipoEnvio) return toast.error("Selecciona el tipo de entrega (Ciudad o Departamento)");
    if (items.length === 0) return toast.error("Agrega al menos un producto");
    for (const it of items) {
      if (!it.product_id || Number(it.cantidad) <= 0) return toast.error("Completa los productos");
    }
    const anticipoNum = Number(anticipo) || 0;
    // Fase F 2.1 — Recordatorio explícito de anticipo antes de guardar
    if (anticipoNum <= 0 && !anticipoConfirmado) {
      setShowAnticipoReminder(true);
      return;
    }
    if (anticipoNum > 0) {
      if (!anticipoMetodo) return toast.error("Selecciona el método de pago del anticipo");
      if (!anticipoBanco && !anticipoCajaId) return toast.error("Selecciona el banco o la caja donde ingresó el pago");
      if (!anticipoAuth.trim()) return toast.error("Ingresa el número de autorización del anticipo");
      if (!anticipoBoleta) return toast.error("Sube la foto de la boleta / comprobante del anticipo");
    }
    setBusy(true);
    try {
      await api.post("/orders", {
        cliente_id: clienteId,
        direccion,
        fecha_entrega: fechaEntrega,
        fecha_entrega_hasta: fechaEntregaHasta,
        envio_id: envioId || null,
        tipo_envio: tipoEnvio,
        items: items.map(({ key, precio_base, ...rest }) => ({
          ...rest,
          cantidad: Number(rest.cantidad),
          precio_unit: Number(rest.precio_unit),
          descuento: Number(rest.descuento || 0),
          variant_id: rest.variant_id || null,
          color: rest.color || "",
          estilo: rest.estilo || "",
          tela: rest.tela || "",
          tamano: rest.tamano || "",
          modificadores: (rest.modificadores || []).map((m) => ({
            id: m.id, nombre: m.nombre, tipo: m.tipo, precio_extra: Number(m.precio_extra || 0),
          })),
        })),
        notas,
        anticipo: anticipoNum,
        anticipo_metodo: anticipoNum > 0 ? anticipoMetodo : "",
        anticipo_banco: anticipoNum > 0 ? anticipoBanco : "",
        anticipo_caja_id: anticipoNum > 0 ? anticipoCajaId : "",
        anticipo_autorizacion: anticipoNum > 0 ? anticipoAuth.trim() : "",
        anticipo_boleta_base64: anticipoNum > 0 ? anticipoBoleta : "",
      });
      toast.success("Pedido creado");
      setOpen(false);
      resetForm();
      setAnticipoConfirmado(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const filtered = orders.filter((o) => {
    const s = q.trim().toLowerCase();
    // Date range filter (uses fecha_entrega_hasta if set, otherwise fecha)
    if (fDesde || fHasta) {
      const ref = (o.fecha_entrega || o.fecha || "").slice(0, 10);
      if (fDesde && ref && ref < fDesde) return false;
      if (fHasta && ref && ref > fHasta) return false;
      if (!ref) return false;
    }
    if (!s) return true;
    return (o.numero || "").toLowerCase().includes(s) || (o.cliente_nombre || "").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="pedidos-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Operación</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1">Hoja de Pedidos</h1>
          <p className="text-zinc-500 mt-2 text-sm">Cada pedido se vincula automáticamente a fabricación, empaque, entrega y facturación.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selected.size > 0 && (
            <>
              <Button
                variant="outline"
                onClick={exportSelected}
                className="gap-2 border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                data-testid="export-selected-btn"
              >
                <FileSpreadsheet className="w-4 h-4" /> Exportar {selected.size} a Excel
              </Button>
              <Button
                onClick={exportReporteRuta}
                disabled={busyReporte}
                className="gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-white disabled:opacity-40"
                data-testid="export-reporte-ruta-btn"
              >
                <FileSpreadsheet className="w-4 h-4" /> Reporte de Ruta ({selected.size})
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => setShowFactRapida(true)} className="gap-2 border-emerald-600 text-emerald-800 hover:bg-emerald-50" data-testid="btn-factura-rapida">
            <Receipt className="w-4 h-4" /> Factura Rápida
          </Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#002FA7] hover:bg-[#00227A] gap-2" data-testid="new-order-btn">
              <Plus className="w-4 h-4" /> Nuevo pedido
            </Button>
          </DialogTrigger>          <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nuevo pedido</DialogTitle>
              <DialogDescription>Se creará con estado “creado” y se enviará a fabricación.</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 md:col-span-1">
                <Label>Cliente</Label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <Select value={clienteId} onValueChange={onClienteChange}>
                      <SelectTrigger data-testid="order-cliente"><SelectValue placeholder="Selecciona un cliente" /></SelectTrigger>
                      <SelectContent>
                        {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                        {customers.length === 0 && <div className="p-3 text-xs text-zinc-500">Registra clientes primero.</div>}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={() => setNewCliOpen(true)} className="gap-1 h-10 px-3" data-testid="new-cliente-btn">
                    <Plus className="w-3.5 h-3.5" /> Nuevo
                  </Button>
                </div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label>Fecha de entrega — Desde <span className="text-red-600">*</span></Label>
                <Input
                  type="date"
                  value={fechaEntrega}
                  onChange={(e) => {
                    setFechaEntrega(e.target.value);
                    if (fechaEntregaHasta && e.target.value > fechaEntregaHasta) {
                      setFechaEntregaHasta(e.target.value);
                    }
                  }}
                  required
                  data-testid="order-fecha-desde"
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label>Fecha de entrega — Hasta <span className="text-red-600">*</span></Label>
                <Input
                  type="date"
                  value={fechaEntregaHasta}
                  onChange={(e) => setFechaEntregaHasta(e.target.value)}
                  min={fechaEntrega || undefined}
                  required
                  data-testid="order-fecha-hasta"
                />
              </div>
              <div className="col-span-2">
                <Label>Dirección de entrega</Label>
                <Input value={direccion} onChange={(e) => setDireccion(e.target.value)} placeholder="Dirección completa" data-testid="order-direccion" />
              </div>
              <div className="col-span-2">
                <Label className="mb-2 block">Tipo de entrega <span className="text-red-600">*</span></Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => { setTipoEnvio("ciudad"); setEnvioId(""); }}
                    className={`rounded-md border p-3 text-left transition-colors ${
                      tipoEnvio === "ciudad"
                        ? "border-[#002FA7] bg-[#002FA7]/5 ring-1 ring-[#002FA7]"
                        : "border-zinc-200 hover:border-zinc-300"
                    }`}
                    data-testid="tipo-ciudad"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Opción 1</div>
                    <div className="font-manrope font-black text-base mt-0.5">Entregas Ciudad</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">Entregado por el piloto en la ciudad.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTipoEnvio("departamento"); setEnvioId(""); }}
                    className={`rounded-md border p-3 text-left transition-colors ${
                      tipoEnvio === "departamento"
                        ? "border-[#8B5CF6] bg-[#8B5CF6]/5 ring-1 ring-[#8B5CF6]"
                        : "border-zinc-200 hover:border-zinc-300"
                    }`}
                    data-testid="tipo-departamento"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Opción 2</div>
                    <div className="font-manrope font-black text-base mt-0.5">Entregas Departamento</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">Empacado y enviado a la agencia.</div>
                  </button>
                </div>
              </div>
              <div className="col-span-2">
                <Label>Método de envío ({tipoEnvio === "ciudad" ? "ciudad" : "departamento / expreso"})</Label>
                <Select value={envioId} onValueChange={setEnvioId}>
                  <SelectTrigger data-testid="order-envio"><SelectValue placeholder="Selecciona envío" /></SelectTrigger>
                  <SelectContent>
                    {shipping
                      .filter((s) => tipoEnvio === "ciudad" ? s.tipo === "ciudad" : (s.tipo === "departamento" || s.tipo === "expreso"))
                      .map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.nombre} · {s.tipo} · {formatMoney(s.precio)}
                        </SelectItem>
                      ))}
                    {shipping.filter((s) => tipoEnvio === "ciudad" ? s.tipo === "ciudad" : (s.tipo === "departamento" || s.tipo === "expreso")).length === 0 && (
                      <div className="p-3 text-xs text-zinc-500">
                        No hay métodos de envío tipo &quot;{tipoEnvio}&quot;. Créalos en Métodos de Envío.
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <div className="flex items-center justify-between mt-2 mb-2">
                  <Label className="text-xs uppercase tracking-widest">Productos</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn">
                    <Plus className="w-3 h-3" /> Agregar producto
                  </Button>
                </div>

                {items.length === 0 && (
                  <div className="text-xs text-zinc-400 text-center border border-dashed rounded-md py-6">
                    Sin productos en este pedido. Agrega al menos uno.
                  </div>
                )}

                <div className="space-y-2">
                  {items.map((it, idx) => {
                    const product = products.find((p) => p.id === it.product_id);
                    const optArr = (arr) => (arr || []);
                    const hasOpts = product && (optArr(product.opciones_color).length + optArr(product.opciones_estilo).length + optArr(product.opciones_tela).length + optArr(product.opciones_tamano).length) > 0;
                    return (
                      <div key={it.key} className="border border-zinc-200 rounded-md p-3 bg-zinc-50 space-y-2">
                        <div className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-12 md:col-span-5">
                            <Label className="text-[10px] uppercase tracking-widest">Producto</Label>
                            <Select value={it.product_id} onValueChange={(v) => onProductChange(idx, v)}>
                              <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                              <SelectContent>
                                {[...products].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es", { sensitivity: "base" })).map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre} — Q{Number(p.precio||0).toFixed(2)}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-4 md:col-span-1">
                            <Label className="text-[10px] uppercase tracking-widest">Cant</Label>
                            <Input type="number" min={1} value={it.cantidad} onChange={(e) => updateItem(idx, { cantidad: Number(e.target.value) })} />
                          </div>
                          <div className="col-span-4 md:col-span-2">
                            <Label className="text-[10px] uppercase tracking-widest">Precio</Label>
                            <Input type="number" min={0} step="0.01" value={it.precio_unit} onChange={(e) => updateItem(idx, { precio_unit: Number(e.target.value) })} data-testid={`item-precio-${idx}`} />
                          </div>
                          <div className="col-span-3 md:col-span-2">
                            <Label className="text-[10px] uppercase tracking-widest">Desc %</Label>
                            <Input type="number" min={0} max={100} step="0.1" value={it.descuento} onChange={(e) => updateItem(idx, { descuento: Number(e.target.value) })} />
                          </div>
                          <div className="col-span-1 flex justify-end">
                            <Button type="button" variant="ghost" size="icon" className="text-red-600" onClick={() => removeItem(idx)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        {(hasOpts || (product && (modifiersCatalog || []).some((m) => m.activo && (m.aplicar_a_todo || (m.productos_ids||[]).includes(product.id) || (m.categorias||[]).some((c)=>(c||"").toLowerCase()===(product.categoria||"").toLowerCase()))))) && (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-1 border-t border-zinc-200">
                            {(() => {
                              // Filtrar modificadores aplicables al producto
                              const applicable = (modifiersCatalog || []).filter((m) => {
                                if (!m.activo) return false;
                                if (m.aplicar_a_todo) return true;
                                if ((m.productos_ids || []).includes(product.id)) return true;
                                if ((m.categorias || []).some((c) => (c || "").toLowerCase() === (product.categoria || "").toLowerCase())) return true;
                                return false;
                              });
                              const modsByAxis = {};
                              for (const m of applicable) {
                                const t = (m.tipo || "").toLowerCase();
                                if (["color", "tela", "estilo", "tamano"].includes(t)) {
                                  (modsByAxis[t] = modsByAxis[t] || []).push(m);
                                }
                              }
                              const axes = [
                                { axis: "color", label: "Color", opts: product.opciones_color },
                                { axis: "estilo", label: "Estilo", opts: product.opciones_estilo },
                                { axis: "tela", label: "Tela", opts: product.opciones_tela },
                                { axis: "tamano", label: "Tamaño", opts: product.opciones_tamano },
                              ].filter((x) => optArr(x.opts).length > 0 || (modsByAxis[x.axis] || []).length > 0);
                              return axes.map(({ axis, label, opts }) => {
                                const nativeOpts = optArr(opts);
                                const axisMods = modsByAxis[axis] || [];
                                // Determinar valor actual: si hay un modificador seleccionado de este axis usarlo, si no it[axis]
                                const selectedMod = (it.modificadores || []).find((mm) => (mm.tipo || "").toLowerCase() === axis);
                                const curValue = selectedMod ? `mod:${selectedMod.id}` : (it[axis] ? `nat:${it[axis]}` : "");
                                const onPick = (v) => {
                                  if (v.startsWith("mod:")) {
                                    const mid = v.slice(4);
                                    const mod = axisMods.find((m) => m.id === mid);
                                    if (!mod) return;
                                    // Guardar en modificadores, remover previa del mismo axis
                                    const others = (it.modificadores || []).filter((mm) => (mm.tipo || "").toLowerCase() !== axis);
                                    updateItem(idx, {
                                      modificadores: [...others, { id: mod.id, nombre: mod.nombre, tipo: mod.tipo, precio_extra: Number(mod.precio_extra || 0) }],
                                      [axis]: mod.nombre,
                                    });
                                  } else if (v.startsWith("nat:")) {
                                    // Nativa: limpiar modificador previo de este axis
                                    const others = (it.modificadores || []).filter((mm) => (mm.tipo || "").toLowerCase() !== axis);
                                    updateItem(idx, { modificadores: others });
                                    onVariantChange(idx, axis, v.slice(4));
                                  }
                                };
                                return (
                                  <div key={axis}>
                                    <Label className="text-[10px] uppercase tracking-widest">{label}</Label>
                                    <Select value={curValue} onValueChange={onPick}>
                                      <SelectTrigger className="h-9" data-testid={`axis-${axis}-${idx}`}><SelectValue placeholder="—" /></SelectTrigger>
                                      <SelectContent>
                                        {nativeOpts.map((o) => (
                                          <SelectItem key={`nat:${o.nombre}`} value={`nat:${o.nombre}`}>
                                            {o.nombre}{Number(o.precio_extra || 0) !== 0 ? ` (+Q${Number(o.precio_extra).toFixed(2)})` : ""}
                                          </SelectItem>
                                        ))}
                                        {axisMods.map((m) => (
                                          <SelectItem key={`mod:${m.id}`} value={`mod:${m.id}`}>
                                            {m.nombre}{Number(m.precio_extra || 0) !== 0 ? ` (+Q${Number(m.precio_extra).toFixed(2)})` : ""}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        )}
                        {product && (() => {
                          // Solo mostrar como botones los modificadores cuyo tipo NO sea un axis (color/tela/estilo/tamano)
                          const applicable = (modifiersCatalog || []).filter((m) => {
                            if (!m.activo) return false;
                            const t = (m.tipo || "").toLowerCase();
                            if (["color", "tela", "estilo", "tamano"].includes(t)) return false;
                            if (m.aplicar_a_todo) return true;
                            if ((m.productos_ids || []).includes(product.id)) return true;
                            if ((m.categorias || []).some((c) => (c || "").toLowerCase() === (product.categoria || "").toLowerCase())) return true;
                            return false;
                          });
                          if (applicable.length === 0) return null;
                          const selected = it.modificadores || [];
                          const isSelected = (mid) => selected.some((x) => x.id === mid);
                          const toggle = (mod) => {
                            const already = isSelected(mod.id);
                            const next = already
                              ? selected.filter((x) => x.id !== mod.id)
                              : [...selected, { id: mod.id, nombre: mod.nombre, tipo: mod.tipo, precio_extra: Number(mod.precio_extra || 0) }];
                            updateItem(idx, { modificadores: next });
                          };
                          return (
                            <div className="pt-2 border-t border-zinc-200" data-testid={`item-modificadores-${idx}`}>
                              <Label className="text-[10px] uppercase tracking-widest text-zinc-600">Modificadores adicionales</Label>
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {applicable.map((m) => {
                                  const sel = isSelected(m.id);
                                  const extra = Number(m.precio_extra || 0);
                                  return (
                                    <button
                                      key={m.id}
                                      type="button"
                                      onClick={() => toggle(m)}
                                      data-testid={`modificador-toggle-${idx}-${m.id}`}
                                      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${
                                        sel
                                          ? "bg-zinc-900 text-white border-zinc-900"
                                          : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"
                                      }`}
                                    >
                                      <span className="uppercase tracking-wider text-[9px] opacity-70">{m.tipo}</span>
                                      <span className="font-medium">{m.nombre}</span>
                                      {extra !== 0 && <span className="opacity-80">{extra > 0 ? `+Q${extra.toFixed(2)}` : `Q${extra.toFixed(2)}`}</span>}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="col-span-2">
                <Label>Notas</Label>
                <Textarea rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} placeholder="Instrucciones especiales..." />
              </div>

              <div className="col-span-2 md:col-span-1">
                <Label>Anticipo del cliente (Q)</Label>
                <Input type="number" min={0} step="0.01" value={anticipo}
                       onChange={(e) => setAnticipo(e.target.value)} data-testid="order-anticipo" />
                <p className="text-[11px] text-zinc-500 mt-1">Se resta del total y aparece como &quot;a cobrar&quot; en la hoja del piloto.</p>
              </div>

              {Number(anticipo) > 0 && (
                <>
                  <div className="col-span-2 md:col-span-1">
                    <Label>Forma de pago del anticipo <span className="text-red-600">*</span></Label>
                    <Select value={anticipoMetodo} onValueChange={setAnticipoMetodo}>
                      <SelectTrigger data-testid="anticipo-metodo"><SelectValue placeholder="Selecciona método" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Efectivo">Efectivo</SelectItem>
                        <SelectItem value="Transferencia">Transferencia</SelectItem>
                        <SelectItem value="Tarjeta">Tarjeta</SelectItem>
                        <SelectItem value="Depósito">Depósito</SelectItem>
                        <SelectItem value="QR / Digital">QR / Digital</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <Label>Cuenta (Banco o Caja) <span className="text-red-600">*</span></Label>
                    <Select
                      value={anticipoCajaId ? `caja:${anticipoCajaId}` : (anticipoBanco ? `banco:${anticipoBanco}` : "")}
                      onValueChange={(v) => {
                        if (v.startsWith("banco:")) { setAnticipoBanco(v.slice(6)); setAnticipoCajaId(""); }
                        else if (v.startsWith("caja:")) { setAnticipoCajaId(v.slice(5)); setAnticipoBanco(""); }
                      }}
                    >
                      <SelectTrigger data-testid="anticipo-cuenta"><SelectValue placeholder="Selecciona banco o caja" /></SelectTrigger>
                      <SelectContent>
                        {bancos.length > 0 && (
                          <div className="text-[9px] uppercase tracking-widest text-zinc-400 px-2 pt-1.5 pb-0.5 font-semibold">Bancos</div>
                        )}
                        {bancos.map((b) => <SelectItem key={`b-${b.id}`} value={`banco:${b.nombre}`}>🏦 {b.nombre}</SelectItem>)}
                        {cajas.length > 0 && (
                          <div className="text-[9px] uppercase tracking-widest text-zinc-400 px-2 pt-1.5 pb-0.5 font-semibold">Cajas</div>
                        )}
                        {cajas.map((c) => <SelectItem key={`c-${c.id}`} value={`caja:${c.id}`}>💰 {c.nombre}</SelectItem>)}
                        {bancos.length === 0 && cajas.length === 0 && (
                          <SelectItem value="__disabled__" disabled>Configura bancos o cajas primero</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label>N° de autorización / referencia <span className="text-red-600">*</span></Label>
                    <Input value={anticipoAuth} onChange={(e) => setAnticipoAuth(e.target.value)}
                           placeholder="Ej. 000123456, comprobante, folio, etc." data-testid="anticipo-auth" required />
                    <p className="text-[11px] text-zinc-500 mt-1">Se generará automáticamente una <b>factura</b> por el monto del anticipo.</p>
                  </div>
                  <div className="col-span-2">
                    <Label>Foto de la boleta / comprobante <span className="text-red-600">*</span></Label>
                    <input
                      type="file" accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.size > 6 * 1024 * 1024) { toast.error("Imagen muy grande (máx 6MB)"); return; }
                        const reader = new FileReader();
                        reader.onload = () => setAnticipoBoleta(reader.result);
                        reader.readAsDataURL(f);
                      }}
                      className="text-xs mt-1"
                      data-testid="anticipo-boleta-input"
                    />
                    {anticipoBoleta && (
                      <div className="mt-2 flex items-start gap-2">
                        <img src={anticipoBoleta} alt="boleta" className="max-h-32 max-w-[120px] object-contain rounded border border-zinc-200" />
                        <button type="button" onClick={() => setAnticipoBoleta("")} className="text-xs text-red-600 underline">Quitar</button>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3 bg-zinc-900 text-white rounded-md p-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400">Subtotal</div>
                  <div className="font-manrope font-black text-lg">{formatMoney(totals.subtotal)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400">Envío</div>
                  <div className="font-manrope font-black text-lg">{formatMoney(totals.envioPrecio)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400">Anticipo</div>
                  <div className="font-manrope font-black text-lg">{formatMoney(Number(anticipo) || 0)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400">A cobrar</div>
                  <div className="font-manrope font-black text-2xl">{formatMoney(totals.total - (Number(anticipo) || 0))}</div>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button onClick={save} disabled={busy} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-order-btn">
                {busy ? "Creando..." : "Crear pedido"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      {/* Tipo tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-zinc-200 -mb-2">
        {TIPO_TABS.map((t) => (
          <button
            key={t.value || "all"}
            type="button"
            onClick={() => setTipoFilter(t.value)}
            data-testid={`tab-tipo-${t.value || "all"}`}
            className={`px-3 py-2 text-sm border-b-2 transition-colors -mb-px ${
              tipoFilter === t.value
                ? "border-[#002FA7] text-[#002FA7] font-semibold"
                : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}
          >
            {t.label}
            <span className="ml-1 text-[10px] bg-zinc-100 rounded-full px-1.5 py-0.5">{orders.filter(t.count).length}</span>
          </button>
        ))}
        {user?.role === "vendedor" && (
          <button
            type="button"
            onClick={() => setMineOnly(!mineOnly)}
            data-testid="toggle-mine"
            className={`ml-auto text-xs uppercase tracking-widest font-semibold px-3 py-1.5 rounded-full border transition-colors ${
              mineOnly
                ? "bg-[#002FA7] text-white border-[#002FA7]"
                : "border-zinc-300 text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            {mineOnly ? "✓ Solo mis pedidos" : "Solo mis pedidos"}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden" data-testid="pedidos-vista-toggle">
          <button
            type="button"
            onClick={() => setVista("actual")}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest font-black ${vista === "actual" ? "bg-black text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
            data-testid="pedidos-tab-actual"
          >Actual</button>
          <button
            type="button"
            onClick={() => setVista("archivados")}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest font-black ${vista === "archivados" ? "bg-black text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
            data-testid="pedidos-tab-archivados"
          >Pedidos Archivados</button>
        </div>
        {vista === "archivados" && (
          <span className="text-[10px] uppercase tracking-widest font-black text-zinc-500 bg-amber-100 border border-amber-300 rounded px-2 py-1">
            Solo lectura — pedidos 100% facturados y cancelados de cierres anteriores
          </span>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-2 md:items-end flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nombre, número pedido, teléfono, zona, departamento…"
                 className="pl-9" onKeyDown={(e) => e.key === "Enter" && load()} data-testid="order-search" />
        </div>
        <DateRangeFilter desde={fDesde} hasta={fHasta} setDesde={setFDesde} setHasta={setFHasta} testidPrefix="ped-filter" />
        <Select value={estado || "all"} onValueChange={(v) => setEstado(v === "all" ? "" : v)}>
          <SelectTrigger className="md:w-56" data-testid="order-filter-estado"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {ESTADOS.slice(1).map((e) => <SelectItem key={e} value={e}>{e[0].toUpperCase() + e.slice(1)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant={pendienteFact ? "default" : "outline"}
          className={pendienteFact ? "bg-orange-600 hover:bg-orange-700 text-white gap-2" : "gap-2"}
          onClick={() => setPendienteFact((v) => !v)}
          data-testid="filter-pendiente-facturar"
        >
          <Receipt className="w-4 h-4" />
          {pendienteFact ? "Solo pendientes facturar" : "Pendientes de facturar"}
        </Button>
      </div>

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-100 text-zinc-600 sticky top-0 z-10 shadow-sm">
              <tr className="text-left">
                <th className="px-2 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((o) => selected.has(o.id))}
                    onChange={(e) => {
                      const n = new Set(selected);
                      if (e.target.checked) filtered.forEach((o) => n.add(o.id));
                      else filtered.forEach((o) => n.delete(o.id));
                      setSelected(n);
                    }}
                    data-testid="select-all-orders"
                  />
                </th>
                {["Pedido", "Se envía", "Productos", "Notas", "Cliente", "Entrega y guía", "Estados", "Pago / Factura"].map((h) => (
                  <th key={h} className="px-2 py-2 text-[10px] uppercase tracking-wider font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center text-zinc-400 py-10">
                  <ClipboardList className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                  Sin pedidos que mostrar.
                </td></tr>
              )}
              {filtered.slice(0, verN).map((o) => {
                const atraso = atrasoInfo(o);
                const atrasoCls = rowClassForAtraso(o);
                const entregado = o.estado_entrega === "entregado";
                const cancelado = o.estado === "cancelado" || o.anulado;
                const facturadoFull = o.estado_facturacion === "facturado";
                // Ronda 24 — Precedencia de colores:
                //   cancelado(corinto) > 100% facturado(AZUL MARINO OSCURO) > entregado(rosa) > se envía(verde) > atraso > default
                //   El pedido 100% facturado se pinta de azul marino oscuro con TODAS las letras blancas.
                const canceladoCls = cancelado ? "bg-rose-900 hover:bg-rose-800 text-white" : null;
                const facturadoCls = facturadoFull ? "bg-blue-950 hover:bg-blue-900 text-white [&_[data-testid^=guias-seg-]]:text-sky-200" : null;
                const depto = o.se_envia && ["departamento", "expreso"].includes(o.envio_tipo);
                const cr = o.col_resumen;
                const enRutaDepto = depto && !entregado && cr?.total > 0 && cr.colocados >= cr.total;
                const parcialDepto = depto && !entregado && (cr?.colocados || 0) > 0 && !enRutaDepto;
                const entregadoCls = entregado ? (depto ? "bg-[#C8A77F] hover:bg-[#BC9970] [&_.text-zinc-400]:text-zinc-800" : "bg-pink-200 hover:bg-pink-300") : null;
                const enRutaCls = enRutaDepto ? "bg-[#C0C0C0] hover:bg-[#B5B5B5] [&_.text-zinc-400]:text-zinc-800" : parcialDepto ? "bg-sky-200 hover:bg-sky-300" : null;
                const seEnviaCls = (o.se_envia && !entregado)
                  ? (o.envio_tipo !== "ciudad" ? "bg-[#E3EFFF] hover:bg-[#D3E5FF]" : "bg-green-200 hover:bg-green-300")
                  : null;
                const baseCls = canceladoCls
                  ? canceladoCls
                  : facturadoCls
                    ? facturadoCls
                    : entregadoCls
                      ? entregadoCls
                      : enRutaCls || (seEnviaCls
                        ? seEnviaCls
                        : atrasoCls
                          ? atrasoCls
                          : "hover:bg-zinc-50");
                return (
                <tr
                  key={o.id}
                  className={`align-top ${baseCls} ${(o.prioridad_fabricacion || o.prioridad_empaque || o.prioridad_entrega) ? "outline outline-2 outline-red-600 -outline-offset-2" : ""}`}
                  data-testid={`order-row-${o.numero}`}
                >
                  <td className="px-2 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(o.id)}
                      onChange={() => toggleSelect(o.id)}
                      data-testid={`select-order-${o.numero}`}
                    />
                  </td>
                  <td className="px-2 py-2">
                    <div className="font-mono text-xs font-bold">{o.numero}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">{formatDate(o.fecha)}</div>
                    <div className="mt-1">
                      <TipoEnvioInline
                        order={o}
                        shipping={shipping}
                        puedeEditar={user?.role === "admin" || user?.role === "gerente_admin"}
                        onChanged={(next) => setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, ...next } : x))}
                      />
                    </div>
                    <div className="text-[10px] leading-tight mt-1 max-w-[90px] break-words" data-testid={`vendedora-${o.numero}`}>
                      Vendedora: {o.creado_por_nombre || o.creado_por || "—"}
                    </div>
                  </td>
                  <td className="px-1 py-2">
                    <SeEnviaToggle
                      order={o}
                      onChanged={(next) => setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, ...next } : x))}
                    />
                  </td>
                  <td className="px-1.5 py-2 min-w-[115px] max-w-[220px]">
                    <ProductosCell o={o} />
                  </td>
                  <td className="px-1.5 py-2 min-w-[85px] max-w-[160px] text-[11px]">
                    <EditableNote
                      orderId={o.id}
                      initial={o.notas_hoja || ""}
                      onSaved={(v) => {
                        setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, notas_hoja: v } : x));
                      }}
                    />
                    {o.notas && (
                      <div className="mt-1 text-[10px] text-zinc-500 italic border-l-2 border-zinc-300 pl-2">
                        Pedido: {o.notas}
                      </div>
                    )}
                  </td>
                  <td className="px-1.5 py-2 min-w-[100px] max-w-[200px]">
                    <div className="font-bold text-xs uppercase" data-testid={`cliente-${o.numero}`}>{o.cliente_nombre}</div>
                    {[o.cliente_telefono, o.cliente_telefono2].filter(Boolean).map((t, i) => (
                      <div key={i} className="text-[11px] font-semibold tabular-nums" data-testid={`tel-${i + 1}-${o.numero}`}>{t}</div>
                    ))}
                    <div className="text-[11px] whitespace-normal break-words leading-tight">{o.direccion}</div>
                    {(hasRole(user, "admin") || user?.role === "gerente_admin") && <DireccionEdit o={o} onSaved={load} />}
                  </td>
                  <td className="px-1.5 py-2 min-w-[95px]">
                    <FechasCell o={o} />
                    <FechaHist o={o} admin={hasRole(user, "admin") || user?.role === "gerente_admin"} onSaved={load} />
                    {atraso.atrasado && (
                      <div className={`mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${atraso.color === "rojo" ? "bg-red-600 text-white" : "bg-yellow-500 text-black"}`} data-testid={`atraso-${o.numero}`}>
                        Atrasado {atraso.dias}d
                      </div>
                    )}
                    <div className="mt-1">
                      <GuiaCell order={o} onSaved={(v) => setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, numero_guia: v } : x))} />
                    </div>
                  </td>
                  <td className="px-1.5 py-2 min-w-[115px] text-[11px]">
                    <div className="flex items-center gap-1"><span className="w-[58px] shrink-0 text-[10px]">Fabricación:</span>
                    <InlineStatus
                      orderId={o.id}
                      campo="estado_fabricacion"
                      value={o.estado_fabricacion || "pendiente"}
                      options={FABRICACION}
                      canEdit={false}
                      onChanged={load}
                    /></div>
                    {o.estado_fabricacion !== "listo" && <Falta items={faltaFab(o)} testid={`falta-fab-${o.numero}`} />}
                    <div className="flex items-center gap-1 mt-1"><span className="w-[58px] shrink-0 text-[10px]">Empaque:</span>
                    <InlineStatus
                      orderId={o.id}
                      campo="estado_empaque"
                      value={o.estado_empaque || "pendiente"}
                      options={EMPAQUE}
                      canEdit={false}
                      onChanged={load}
                    /></div>
                    {o.estado_empaque !== "empacado" && <Falta items={faltaTodos(o)} testid={`falta-emp-${o.numero}`} />}
                    <ColocacionBadge o={o} />
                    <LiquidacionBadge o={o} />
                    <div className="flex items-center gap-1 mt-1"><span className="w-[58px] shrink-0 text-[10px]">Entrega:</span>
                    <InlineStatus
                      orderId={o.id}
                      campo="estado_entrega"
                      value={o.estado_entrega || "pendiente"}
                      options={ENTREGA}
                      canEdit={false}
                      onChanged={load}
                    /></div>
                    <Falta items={faltaEntrega(o)} testid={`falta-ent-${o.numero}`} />
                    <GuiasSeg o={o} />
                    <ColResumenAlertas o={o} />
                    <div className="flex flex-wrap gap-1 mt-1">
                      {["fabricacion", "empaque", "entrega"].map((a) => (
                        <PrioridadToggle key={a} corto order={o} area={a} onChanged={(next) => setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, ...next } : x))} />
                      ))}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-start gap-1">
                    <PagoCell o={o} />
                    <Link to={`/pedidos/${o.id}`} onClick={() => { try { sessionStorage.setItem("pedidos_return_path", window.location.pathname + window.location.search); } catch {} }} className="text-[#002FA7] text-[10px] font-black uppercase inline-flex items-center" data-testid={`view-${o.numero}`}>
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                    </div>
                    {(() => {
                      const ef = o.estado_facturacion || "pendiente_facturar";
                      const map = {
                        pendiente_facturar: { label: "Pendiente", cls: "bg-zinc-200 text-zinc-800" },
                        parcial: { label: "Parcial", cls: "bg-yellow-100 text-yellow-900" },
                        facturado: { label: "Facturado", cls: "bg-emerald-100 text-emerald-900" },
                        falta_pago: { label: "Falta pago", cls: "bg-orange-100 text-orange-900" },
                        pagado: { label: "Pagado", cls: "bg-emerald-100 text-emerald-900" },
                      };
                      const s = map[ef] || map.pendiente_facturar;
                      const nFact = (o.facturas || []).length;
                      const saldo = Number(o.saldo_pendiente ?? 0);
                      return (
                        <div
                          className="flex items-center gap-1 mt-1 text-[10px]"
                          title="Solo consulta: se actualiza desde Facturación"
                          data-testid={`facturar-link-${o.numero}`}
                        >
                          <span>Factura:</span>
                          <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${s.cls}`} data-testid={`facturacion-badge-${o.numero}`}>
                            {s.label}
                          </span>
                          {nFact > 0 && (
                            <div className="text-[9px] text-zinc-500 mt-0.5 leading-tight">
                              {nFact}f{saldo > 0 && <span className="text-orange-700 font-semibold"> · Q{saldo.toFixed(0)}</span>}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length > verN && (
            <div className="px-4 py-3 border-t bg-zinc-50 flex items-center justify-between gap-3">
              <span className="text-xs text-zinc-600" data-testid="pedidos-mostrando">Mostrando {verN} de {filtered.length}</span>
              <Button size="sm" variant="outline" onClick={() => setVerN((n) => n + 200)} data-testid="pedidos-ver-mas">Ver más pedidos</Button>
            </div>
          )}
          {/* Paginación en servidor (30 por página) */}
          {pageMeta.total_pages > 1 && (
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-t bg-zinc-50" data-testid="pedidos-paginacion">
              <div className="text-xs text-zinc-600 tabular-nums">
                Mostrando <b>{((pageMeta.page - 1) * 10000) + 1}</b>–<b>{Math.min(pageMeta.page * 10000, pageMeta.total)}</b> de <b>{pageMeta.total}</b> pedidos
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pageMeta.page <= 1}
                  data-testid="pag-prev"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
                </Button>
                <span className="text-xs font-black tabular-nums">
                  Pág. {pageMeta.page} / {pageMeta.total_pages}
                </span>
                <Button
                  variant="outline" size="sm"
                  onClick={() => setPage((p) => Math.min(pageMeta.total_pages, p + 1))}
                  disabled={pageMeta.page >= pageMeta.total_pages}
                  data-testid="pag-next"
                >
                  Siguiente <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Nuevo cliente rapido */}
      <Dialog open={newCliOpen} onOpenChange={setNewCliOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo cliente</DialogTitle>
            <DialogDescription>Se creará el cliente y quedará seleccionado en el pedido.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest">Nombre</Label>
              <Input value={newCli.nombre} onChange={(e) => setNewCli({ ...newCli, nombre: e.target.value })} data-testid="new-cli-nombre" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs uppercase tracking-widest">Teléfono</Label>
                <Input value={newCli.telefono} onChange={(e) => setNewCli({ ...newCli, telefono: e.target.value })} data-testid="new-cli-tel" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Teléfono 2</Label>
                <Input value={newCli.telefono2} onChange={(e) => setNewCli({ ...newCli, telefono2: e.target.value })} data-testid="new-cli-tel2" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Dirección</Label>
              <Input value={newCli.direccion} onChange={(e) => setNewCli({ ...newCli, direccion: e.target.value })} data-testid="new-cli-dir" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCliOpen(false)}>Cancelar</Button>
            <Button onClick={crearNuevoCliente} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="save-new-cli">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={showAnticipoReminder} onOpenChange={setShowAnticipoReminder}>
        <DialogContent className="max-w-md" data-testid="dialog-anticipo-reminder">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" /> Recordatorio de anticipo
            </DialogTitle>
            <DialogDescription>
              Este pedido se está por guardar <b>sin anticipo</b>. ¿El cliente pagó un anticipo que aún no has registrado?
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-zinc-600 py-2">
            Si tiene anticipo, cancela y captura el monto + comprobante antes de guardar. Si no tiene anticipo, confirma para continuar.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnticipoReminder(false)} data-testid="btn-antic-tiene">
              Sí tiene anticipo — regresar
            </Button>
            <Button
              className="bg-emerald-700 hover:bg-emerald-800"
              onClick={async () => { setAnticipoConfirmado(true); setShowAnticipoReminder(false); setTimeout(() => save(), 50); }}
              data-testid="btn-antic-continuar"
            >
              No tiene anticipo — continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FacturaRapidaDialog
        open={showFactRapida}
        onClose={() => setShowFactRapida(false)}
        customers={customers}
        products={products}
        shipping={shipping}
        bancos={bancos}
        cajas={cajas}
        onCreated={load}
      />
    </div>
  );
}

/**
 * Inline editable Guía/Envío cell.
 * - Editable only for Departamento/Expreso orders.
 * - Ciudad orders show "Auto al exportar Reporte de Ruta" hint (código de envío se genera después).
 */
function GuiaCell({ order, onSaved }) {
  const isDpto = order.envio_tipo !== "ciudad";
  const [value, setValue] = useState(order.numero_guia || "");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => setValue(order.numero_guia || ""), [order.numero_guia]);

  const commit = async () => {
    if (value === (order.numero_guia || "")) { setEditing(false); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/orders/${order.id}/guia`, { numero_guia: value.trim() });
      toast.success(value ? "Guía guardada" : "Guía eliminada");
      onSaved?.(data.numero_guia || "");
    } catch (e) {
      toast.error(formatApiError(e));
      setValue(order.numero_guia || "");
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (!isDpto) {
    if (order.numero_guia) {
      return (
        <div className="font-mono text-[11px] text-zinc-700" data-testid={`ped-guia-${order.numero}`} title="Código de envío generado automáticamente">
          {order.numero_guia}
        </div>
      );
    }
    return (
      <div className="text-[10px] text-zinc-400 italic" data-testid={`ped-guia-empty-${order.numero}`}>
        Auto al exportar<br />Reporte de Ruta
      </div>
    );
  }
  if (editing) {
    return (
      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Escape") { setValue(order.numero_guia || ""); setEditing(false); }
          if (e.key === "Enter") commit();
        }}
        disabled={saving}
        placeholder="Nº de guía"
        className="w-full text-[11px] font-mono rounded border border-blue-300 bg-blue-50 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
        data-testid={`ped-guia-input-${order.numero}`}
      />
    );
  }
  if (value) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="text-left w-full font-mono text-[11px] bg-blue-50 border border-blue-200 rounded px-2 py-1 text-zinc-800 hover:border-blue-400"
        data-testid={`ped-guia-${order.numero}`}
      >
        {value}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="text-[11px] text-zinc-400 hover:text-[#002FA7] inline-flex items-center gap-1"
      data-testid={`ped-guia-add-${order.numero}`}
    >
      <Pencil className="w-3 h-3" /> Agregar guía
    </button>
  );
}


// ----------- Selector inline de tipo de entrega (Ciudad / Departamento) ------------
function TipoEnvioInline({ order, shipping, onChanged, puedeEditar }) {
  const [busy, setBusy] = useState(false);
  const change = async (nuevoTipo) => {
    if (nuevoTipo === (order.envio_tipo || "")) return;
    if (!window.confirm(`¿Cambiar tipo de entrega de "${order.envio_tipo}" a "${nuevoTipo}"?`)) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/orders/${order.id}/envio-tipo`, { tipo_envio: nuevoTipo });
      onChanged(data);
      toast.success(`Tipo de entrega actualizado a ${nuevoTipo}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const isDpto = order.envio_tipo !== "ciudad";
  return (
    <Select value={isDpto ? "departamento" : "ciudad"} onValueChange={change} disabled={busy || !puedeEditar}>
      <SelectTrigger
        className={`h-4 px-1 py-0 border-0 text-[8px] font-semibold uppercase rounded ${
          !isDpto ? "bg-blue-100 text-blue-900" : "bg-violet-100 text-violet-900"
        }`}
        data-testid={`tipo-envio-inline-${order.numero}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="ciudad">Ciudad</SelectItem>
        <SelectItem value="departamento">Departamento</SelectItem>
      </SelectContent>
    </Select>
  );
}

// ----------- Toggle "Se envía / Pendiente / Entregado" para Reporte de Ruta ------------
function SeEnviaToggle({ order, onChanged }) {
  const [busy, setBusy] = useState(false);
  const on = !!order.se_envia;
  const esDepto = order.envio_tipo !== "ciudad";
  const entregado = order.estado_entrega === "entregado";
  const intentos = (order.intentos_entrega || []).length;

  // Si el pedido ya está entregado, mostrar badge fijo NO clickeable
  if (entregado) {
    return (
      <div className="flex flex-col items-start gap-1">
        <span
          className="inline-block text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md bg-emerald-600 text-white border border-emerald-700 select-none cursor-not-allowed"
          title="Pedido ya entregado — no puede reenviarse"
          data-testid={`badge-entregado-${order.numero}`}
        >
          ✓ ENTREGADO
        </span>
        {intentos > 0 && (
          <span className="inline-flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-emerald-800" title="Intentos previos de entrega">
            {intentos} intento{intentos > 1 ? "s" : ""}
          </span>
        )}
      </div>
    );
  }

  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/orders/${order.id}/se-envia`, { se_envia: !on });
      onChanged(data);
      toast.success(!on ? "Marcado como Se envía · agregado al Reporte de Ruta del día" : "Devuelto a Pendiente");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  // Determinar color del badge de intentos según cuántos hay
  const intentoCls =
    intentos >= 3 ? "bg-red-100 text-red-800 border-red-300" :
    intentos === 2 ? "bg-orange-100 text-orange-800 border-orange-300" :
    intentos === 1 ? "bg-amber-100 text-amber-800 border-amber-300" :
    "";

  const lastIntento = (order.intentos_entrega || []).slice(-1)[0];
  const intentoTitle = lastIntento
    ? `Último: ${lastIntento.resultado} · ${(lastIntento.fecha || "").slice(0, 10)}${lastIntento.nota ? " — " + lastIntento.nota : ""}`
    : "";

  return (
    <div className="flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-md border transition-colors ${
          on
            ? (esDepto
              ? "bg-[#E3EFFF] text-[#175CD3] border-[#175CD3] hover:bg-[#D3E5FF]"
              : "bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700")
            : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
        }`}
        data-testid={`se-envia-toggle-${order.numero}`}
      >
        {on ? (esDepto ? (order.estado_entrega === "entregado" ? "Entregado" : (order.col_resumen?.total > 0 && order.col_resumen.colocados >= order.col_resumen.total) ? "En ruta departamento" : (order.col_resumen?.colocados || 0) > 0 ? `Parcial en ruta · ${order.col_resumen.colocados}/${order.col_resumen.total}` : "Se envía a departamento") : "Se envía") : "Pendiente"}
      </button>
      {intentos > 0 && (
        <span
          className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${intentoCls}`}
          title={intentoTitle}
          data-testid={`badge-intentos-${order.numero}`}
        >
          ⚠️ {intentos} intento{intentos > 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
}

// -------------------- Factura Rápida --------------------
function FacturaRapidaDialog({ open, onClose, customers, products, shipping, bancos, cajas, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [clienteId, setClienteId] = useState("");
  const [direccion, setDireccion] = useState("");
  const [productoId, setProductoId] = useState("");
  const [cantidad, setCantidad] = useState(1);
  const [precio, setPrecio] = useState(0);
  const [envioId, setEnvioId] = useState("");
  const [tipoEnvio, setTipoEnvio] = useState("ciudad");
  const [metodo, setMetodo] = useState("Efectivo");
  const [cuenta, setCuenta] = useState(""); // "banco:Xxx" o "caja:id"
  const [monto, setMonto] = useState(0);
  const [autorizacion, setAutorizacion] = useState("");
  const [comentario, setComentario] = useState("");
  const [boleta, setBoleta] = useState("");

  useEffect(() => {
    if (open) { setBusy(false); setClienteId(""); setDireccion(""); setProductoId(""); setCantidad(1); setPrecio(0); setEnvioId(""); setTipoEnvio("ciudad"); setMetodo("Efectivo"); setCuenta(""); setMonto(0); setAutorizacion(""); setComentario(""); setBoleta(""); }
  }, [open]);

  const prod = products.find((p) => p.id === productoId);
  useEffect(() => { if (prod) setPrecio(Number(prod.precio_base || 0)); }, [productoId]); // eslint-disable-line

  const total = Number(cantidad || 0) * Number(precio || 0) + (shipping.find(s => s.id === envioId)?.precio || 0);

  useEffect(() => { setMonto(total); }, [total]);

  const submit = async () => {
    if (!clienteId) return toast.error("Selecciona un cliente");
    if (!direccion.trim()) return toast.error("Ingresa la dirección");
    if (!productoId) return toast.error("Selecciona un producto");
    if (!cantidad || cantidad <= 0) return toast.error("Cantidad inválida");
    if (!precio || precio <= 0) return toast.error("Precio inválido");
    if (!metodo) return toast.error("Método de pago obligatorio");
    if (!cuenta) return toast.error("Selecciona banco o caja");
    if (!autorizacion.trim()) return toast.error("Número de autorización obligatorio");
    if (!boleta) return toast.error("Foto del comprobante obligatoria");
    const banco = cuenta.startsWith("banco:") ? cuenta.slice(6) : "";
    const cajaId = cuenta.startsWith("caja:") ? cuenta.slice(5) : "";
    const today = new Date().toISOString().slice(0, 10);
    setBusy(true);
    try {
      await api.post("/orders", {
        cliente_id: clienteId,
        direccion,
        fecha_entrega: today,
        fecha_entrega_hasta: today,
        envio_id: envioId || null,
        tipo_envio: tipoEnvio,
        items: [{ product_id: productoId, cantidad: Number(cantidad), precio_unit: Number(precio), descuento: 0, modificadores: [] }],
        notas: `[FACTURA RÁPIDA] ${comentario || ""}`.trim(),
        anticipo: Number(monto),
        anticipo_metodo: metodo,
        anticipo_banco: banco,
        anticipo_caja_id: cajaId,
        anticipo_autorizacion: autorizacion.trim(),
        anticipo_boleta_base64: boleta,
      });
      toast.success("Factura Rápida creada");
      onCreated?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-factura-rapida">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-emerald-700" /> Factura Rápida</DialogTitle>
          <DialogDescription>Crea un pedido emergente + factura en un solo paso. Queda vinculado a Hoja de Pedidos, Facturación, Bancos/Cajas y Reportes.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 md:col-span-1">
            <Label>Cliente</Label>
            <Select value={clienteId} onValueChange={(v) => { setClienteId(v); const c = customers.find(x => x.id === v); if (c) setDireccion(c.direccion || ""); }}>
              <SelectTrigger data-testid="fr-cliente"><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
              <SelectContent>
                {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 md:col-span-1">
            <Label>Dirección</Label>
            <Input value={direccion} onChange={(e) => setDireccion(e.target.value)} data-testid="fr-direccion" />
          </div>
          <div className="col-span-2 md:col-span-1">
            <Label>Producto</Label>
            <Select value={productoId} onValueChange={setProductoId}>
              <SelectTrigger data-testid="fr-producto"><SelectValue placeholder="Selecciona producto" /></SelectTrigger>
              <SelectContent>
                {[...products].sort((a, b) => (a.nombre || "").localeCompare(b.nombre || "", "es", { sensitivity: "base" })).map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Cantidad</Label>
            <Input type="number" min="1" value={cantidad} onChange={(e) => setCantidad(e.target.value)} data-testid="fr-cantidad" />
          </div>
          <div>
            <Label>Precio unitario</Label>
            <Input type="number" step="0.01" value={precio} onChange={(e) => setPrecio(e.target.value)} data-testid="fr-precio" />
          </div>
          <div>
            <Label>Envío</Label>
            <Select value={envioId} onValueChange={setEnvioId}>
              <SelectTrigger data-testid="fr-envio"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {shipping.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre} · Q{s.precio}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={tipoEnvio} onValueChange={setTipoEnvio}>
              <SelectTrigger data-testid="fr-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ciudad">Ciudad</SelectItem>
                <SelectItem value="departamento">Departamento</SelectItem>
                <SelectItem value="expreso">Expreso</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm">
            <div className="text-[10px] uppercase tracking-widest text-emerald-800 font-semibold">Total a cobrar</div>
            <div className="text-2xl font-black text-emerald-800">{formatMoney(total)}</div>
          </div>
          <div>
            <Label>Método de pago</Label>
            <Select value={metodo} onValueChange={setMetodo}>
              <SelectTrigger data-testid="fr-metodo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Efectivo">Efectivo</SelectItem>
                <SelectItem value="Transferencia">Transferencia</SelectItem>
                <SelectItem value="Tarjeta">Tarjeta</SelectItem>
                <SelectItem value="Depósito">Depósito</SelectItem>
                <SelectItem value="QR / Digital">QR / Digital</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Banco o Caja</Label>
            <Select value={cuenta} onValueChange={setCuenta}>
              <SelectTrigger data-testid="fr-cuenta"><SelectValue placeholder="Selecciona" /></SelectTrigger>
              <SelectContent>
                {bancos.length > 0 && <div className="text-[9px] uppercase tracking-widest text-zinc-400 px-2 pt-1.5 pb-0.5 font-semibold">Bancos</div>}
                {bancos.map((b) => <SelectItem key={`b-${b.id}`} value={`banco:${b.nombre}`}>🏦 {b.nombre}</SelectItem>)}
                {cajas.length > 0 && <div className="text-[9px] uppercase tracking-widest text-zinc-400 px-2 pt-1.5 pb-0.5 font-semibold">Cajas</div>}
                {cajas.map((c) => <SelectItem key={`c-${c.id}`} value={`caja:${c.id}`}>💰 {c.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>N° autorización / referencia</Label>
            <Input value={autorizacion} onChange={(e) => setAutorizacion(e.target.value)} data-testid="fr-auth" />
          </div>
          <div className="col-span-2">
            <Label>Comentarios (opcional)</Label>
            <Input value={comentario} onChange={(e) => setComentario(e.target.value)} placeholder="Notas del pedido emergente" data-testid="fr-comentario" />
          </div>
          <div className="col-span-2">
            <Label>Foto boleta / comprobante <span className="text-red-600">*</span></Label>
            <input
              type="file" accept="image/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                if (f.size > 6 * 1024 * 1024) return toast.error("Máx 6MB");
                const r = new FileReader(); r.onload = () => setBoleta(r.result); r.readAsDataURL(f);
              }}
              className="text-xs mt-1"
              data-testid="fr-boleta"
            />
            {boleta && <img src={boleta} alt="boleta" className="mt-2 max-h-24 rounded border" />}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={submit} disabled={busy} data-testid="fr-submit">
            {busy ? "Creando..." : `Crear pedido + factura por ${formatMoney(total)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
